/*****************************************************************************
 *   © Copyright 2021   Quantitative Software Management, Inc.               *
 *              --  All rights reserved.  --                                 *
 *   Unpublished -- rights reserved under the Copyright Laws                 *
 *   of the United States.                                                   *
 *                                                                           *
 *   This computer software is protected by trade secret, copyright law,     *
 *   and international treaties and contains proprietary and confidential    *
 *   trade secrets of Quantitative Software Management, Inc.  Unauthorized   *
 *   use, reproduction, or distribution of this program or any portion of    *
 *   it will result in severe civil or criminal penalties and will be        *
 *   prosecuted to the full extent of the law.                               *
 *                                                                           *
 *   Use, duplication, or disclosure by the Government is subject to         *
 *   restrictions as set forth in                                            *
 *      (i) a license agreement incorporated in the contract,                *
 *      (ii) the definition of "restricted rights" in paragraph (c)(1)(ii)   *
 *           of DFARS 252.227-7013 (1988), and                               *
 *      (iii) the definition of "restricted rights" in paragraph (a) of      *
 *            FAR 52.227-14, Alt III (Jun 1987) or                           *
 *      (iv) FAR 52.227-19 (Jun 1987), as applicable.                        *
 *   For purposes of the FAR, this software shall be deemed to               *
 *   be "unpublished" and licensed with disclosure prohibitions.             *
 *   Contractor/Manufacturer is   Quantitative Software Management, Inc.     *
 *                                                                           *
 *****************************************************************************/

import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { saveAs } from 'file-saver';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { finalize, first } from 'rxjs/operators';

import { AppConstants } from '../app.constants';
import { ProductivityRankLabel, ProductivityType, SizeBinLabel, SizeSource, SolutionMethod, SolutionParameter, Stage } from '../app.enums';
import { ErrorConstants } from '../app.errorConstants';
import { BasicSolution } from '../Models/BasicSolution';
import { ChangedFunctionUnit } from '../Models/ChangedFunctionUnit';
import { ChangedMoneyUnit } from '../Models/ChangedMoneyUnit';
import { ExternalGui } from '../Models/ExternalGui';
import { FileDownloadInfo } from '../Models/FileDownloadInfo';
import { MonetaryUnit } from '../Models/Generic-Data';
import { CommittedProjectData, ProjectChange, ProjectViewModel, ProjectInfo } from '../Models/ProjectInfo';
import { QsmServerError } from '../Models/QsmServerError';
import { TrendId } from '../Models/TrendId';
import { AccessInfo, UserView } from '../Models/UserView';
import { ApplicationType, UtilityService } from './Utility.Service';


enum ProjectControllerErrors { MissingSolution = 1, InvalidSolution = 2, ExternalCallError = 10, ExportError = 11 }

@Injectable({
  providedIn: 'root'
})
export class ProjectService {
  constructor(private http: HttpClient, private utilService: UtilityService) {
    this.guid = Math.random();
  }
  public newProjectInfo: Subject<ProjectInfo> = new Subject<ProjectInfo>();
  public guid: number = 0;
  //solutionlog
  public solutionId: number;
  public solutionChanges: any;

  // money unit changes
  private moneyUnitChange: Subject<ChangedMoneyUnit> = new Subject<ChangedMoneyUnit>();
  public MoneyUnitChanged: Observable<ChangedMoneyUnit> = this.moneyUnitChange.asObservable();

  // Project object
  private _projView: BehaviorSubject<ProjectViewModel> = new BehaviorSubject(new ProjectViewModel());
  public currentProjectView: Observable<ProjectViewModel> = this._projView.asObservable();

  public committedData: CommittedProjectData = new CommittedProjectData();

  // Project 'readiness'
  public projInitialized: boolean = false;
  private _projInitialized: Subject<boolean> = new Subject<boolean>();
  public ProjectIsInitialized: Observable<boolean> = this._projInitialized.asObservable();

  //ErrorObject concurrency
  public errorObject: BehaviorSubject<QsmServerError> = new BehaviorSubject<QsmServerError>(null);

  public currentProjectViewModel(): ProjectViewModel | null {
    return this.projInitialized ? this._projView.value : null;
  }

  public currentId(): number | null {
    return this.projInitialized ? this._projView.value.Project["Id"] : null;
  }

  public currentTrendVersion(): string {
    return this.projInitialized ? this._projView.value.CurrentTrendVersionName : "";
  }

  public defaultTrendVersion(): string {
    return this.projInitialized ? this._projView.value.DefaultTrendVersionName : "";
  }

  public get sizeIsValid(): boolean {
    return this.projInitialized ? this._projView.value.SizeIsValid : false;
  }

  public get solutionIsReasonable(): boolean {
    return this.projInitialized ? this._projView.value.SolutionIsReasonable : true;
  }

  public trendConfirmed(conf: string): boolean {
    let result: boolean = false;
    this._projView.value.TrendVersionNeedsConfirmation = false;
    if (conf != "postpone") {
      result = true;
      let url: string = 'api/projects/trendconfirm';
      this.http.post<TrendId>(url, JSON.stringify(conf), this.utilService.JsonOptions()).subscribe(trend => {
        this._projView.value.Project["TrendConfiguration"] = trend;
        this._projView.value.Committed = false;
        this.EmitNextProjectView(this._projView.value, ProjectChange.TRENDCONFIRM);
      },
        theHttpError => {
          let theHttpErrorResponse = theHttpError as HttpErrorResponse;
          let serverError = this.utilService.errorService.getQsmServerError(theHttpErrorResponse);
          let errorHandled = false;
          if (serverError != null) {
            if (+(serverError.errorCode) == ProjectControllerErrors.MissingSolution) {
              this.utilService.errorService.ErrorRedirect(theHttpError, "Missing Solution");;
              errorHandled = true;
            }
            if (serverError.errorCode == ErrorConstants.ConcurrencyViolation) {
              this.errorObject.next(serverError);
            }
            this.handleQSMCustomErrors(theHttpError, 'Failed to save the trend');
            errorHandled = true;
          }
          if (errorHandled == false) {
            this.utilService.errorService.ErrorRedirect(theHttpError, "Failed to save the trend");
          }
        }
      );
    }
    return result;
  }

  public exportProject(withLock: boolean) {
    let exportObj = { withLock };
    this.http.post<ProjectViewModel>('api/projects/export/', exportObj, this.utilService.JsonOptions()).subscribe(pv => {
      let theExtra = pv.Extra;
      if (pv.Project) {
        this.EmitNextProjectView(pv, ProjectChange.SAVE);  //sets pv.Extra = null;
      }
      if (theExtra) {
        const downloadInfo = new FileDownloadInfo(theExtra.content as FileDownloadInfo);
        const theBlob = downloadInfo.asBlob;
        if (theBlob !== null) {
          const fileDownloadName = downloadInfo.FileDownloadName;
          saveAs(theBlob, fileDownloadName);
        }
        //}
      }
    },
      theHttpError => {
        //this._projInitialized.next(false); //is this what we want?
        let theHttpErrorResponse = theHttpError as HttpErrorResponse;
        let serverError = this.utilService.errorService.getQsmServerError(theHttpErrorResponse);
        let errorHandled = false;
        if (serverError != null) {
          if (serverError.extra) {
            let pv = serverError.extra as ProjectViewModel;
            if (pv.Project) {
              this.EmitNextProjectView(pv, ProjectChange.SAVE); //initialize?
            }
          }
          if (serverError.errorCode == ErrorConstants.UserAccessViolation) {
            this.handleQSMCustomErrors(theHttpError, 'Export Project Access Violation');
            errorHandled = true;
          }
          else if (+(serverError.errorCode) == ProjectControllerErrors.ExportError) {
            alert(serverError.errorMessage);
            errorHandled = true;
          }
        }
        if (!errorHandled) {
          this.utilService.errorService.ErrorRedirect(theHttpError);
        }
      }
    );//subscribe
  }

  public suggestedSolutionName(solnNumber: number): string {
    if (!this.projInitialized) {
      return '';
    }

    let name = 'SOL ' + (solnNumber < 10 ? '0' + solnNumber.toString() : solnNumber.toString()) + ':  ';
    let soln = this._projView.value.Project["CurrentSolution"];
    let method = soln['SolutionMethod'];
    let moneyUnit = this._projView.value.Project['ProjectMonetaryUnit']['Acronym'];

    if (method === SolutionMethod.TREND) {
      name += 'Trend based on Eff Size of ' + soln['SizeUser']['effectiveSize'];
    } else if (method === SolutionMethod.CONSTRAINED) {
      name += this.ConstraintPhrase(soln['SolutionParameterType'], soln['SolutionParameterValue'], moneyUnit, true);
    } else if (method === SolutionMethod.BOXED) {
      name += 'Time Boxed ' +
        this.ConstraintPhrase(soln['SolutionParameterType'], soln['SolutionParameterValue'], moneyUnit) + ' and  ' + soln['FeasibilityTimeGoal'] + ' months.';
    } else {
      name += 'Feasibility ' +
        this.ConstraintPhrase(soln['SolutionParameterType'], soln['SolutionParameterValue'], moneyUnit) + ' and  ' + soln['FeasibilityTimeGoal'] + ' months.';
    }
    return name;
  }

  public suggestedSolutionDescription(): string {
    if (!this.projInitialized) {
      return '';
    }

    let now = new Date(Date.now()).toLocaleDateString();
    let uv = this._projView.value.User;
    let desc = 'Originally logged by ' + uv.DisplayName + ' on ' + now;

    let soln = this._projView.value.Project["CurrentSolution"];
    let method = soln['SolutionMethod'];

    let piIsCalculated = method == SolutionMethod.TREND || method == SolutionMethod.SOLVEFORPI;
    let pi = piIsCalculated ? ' with an output PI of ' + soln['pi'] :
      (soln['ProductivityType'] == ProductivityType.INDEX ?
        ' with an input PI of ' + soln['ProductivityIndex'] :
        ' with an input PI Rank of ' + ProductivityRankLabel.get(soln['ProductivityRank']));

    let sizeIsCalculated = method == SolutionMethod.BOXED;
    let omitSize = method == SolutionMethod.TREND;     //already included in name

    let fu = this._projView.value.Project['FunctionUnit']['Acronym'];
    let size = sizeIsCalculated ? ' and an output Eff Size of ' + soln['SizeUser']['effectiveSize'] + ' ' + fu :
      (soln['SizingSource'] == SizeSource.BIN ?
        ' and an input Size Bin of ' + SizeBinLabel.get(soln['SizingBin']) :
        ' and an input Size of ' + soln['SizeUser']['effectiveSize'] + ' ' + fu);
    desc += pi + (omitSize ? '' : size) + '.';
    return desc;
  }

  private ConstraintPhrase(parmType: number, goalValue: number, moneyUnit: string, isSingleGoal: boolean = false): string {
    let unit = '';
    switch (parmType) {
      case SolutionParameter.COST:
        unit = moneyUnit;
        break;
      case SolutionParameter.EFFORT:
        unit = 'hrs';
        break;
      case SolutionParameter.DURATION:
        unit = 'months';
        break;
      case SolutionParameter.STAFF:
        unit = 'avg staff';
        break;
    }
    let phrase = isSingleGoal ? 'Design to Goal of ' : 'constrained to ';
    return goalValue !== null ? phrase + goalValue + ' ' + unit : ' n/a';
  }

  public getProj(id: number, reason: ProjectChange = ProjectChange.NO_CHANGE): void {
    this.http.get<ProjectViewModel>('api/projects/current/' + id, this.utilService.AjaxOptions())
      .subscribe(pv => {
        this.InitializeAndEmit(pv, reason);
      },
        theHttpError => {
          this.handleQSMCustomErrors(theHttpError, 'Failed to retrieve the project');
        }
      );
  }

  public setProj(id: number, reason: ProjectChange = ProjectChange.NO_CHANGE): void {
   let putCurrent = 'api/projects/current/' + id;
    this.http.put<ProjectViewModel>(putCurrent, '', this.utilService.AjaxOptions())
      .subscribe(pv => {
        this.InitializeAndEmit(pv, reason);
      },
        theHttpError => {
          this.handleQSMCustomErrors(theHttpError, 'Failed to Set Current Project');
        }
      );
  }

  private handleQSMCustomErrors(theHttpError: any, message: string) {
    this._projInitialized.next(false);
    let theHttpErrorResponse = theHttpError as HttpErrorResponse;
    let errorHandled = false;
    let serverError = this.utilService.errorService.getQsmServerError(theHttpErrorResponse);
    if (serverError != null) {
      if (serverError.errorCode == ErrorConstants.ProjectMissingFromDatabase || serverError.errorCode == ErrorConstants.UserAccessViolation) {
        errorHandled = true;
        let errorNotification = "ProjectDeleted";
        if (serverError.errorCode == ErrorConstants.UserAccessViolation) {
          errorNotification = "UnauthorizedAccess";
        }
        this.errorObject.next(serverError);
        let projectList = `Views/Services/Portfolio/Portfolio_Default.aspx?${ErrorConstants.ERROR_NOTIFICATION_PARM}=${errorNotification}`;
        this.utilService.NavigateToAspx(`/${AppConstants.WEBAPP_NAME}/${projectList}`);
      }
      if (+(serverError.errorCode) == ProjectControllerErrors.MissingSolution) {
        errorHandled = true;
        this.utilService.errorService.ErrorRedirect(theHttpError, message, true);
      }
    }
    if (!errorHandled) {
      this.utilService.errorService.ErrorRedirect(theHttpError, message, true);
    }
  }

  private SetCommittedData(pvm: ProjectViewModel) {
    if (pvm.Committed && pvm.Project) {
      this.committedData.ProjectId = pvm.Project["Id"];
      this.committedData.PrimaryResponsibilityId = pvm.Project['PrimaryResponsibility'] == null ? 0 : pvm.Project['PrimaryResponsibility']['Id'];
    }
  }

  public InitializeAndEmit(pvm: ProjectViewModel, reason: ProjectChange = ProjectChange.NO_CHANGE): void {
    this.projInitialized = true;
    pvm.ChangeType = reason;
    this.SetCommittedData(pvm);
    this._projView.next(pvm);
    this._projInitialized.next(true);
    //pvm.Committed = null;
    pvm.Extra = null;
  }

  private EmitNextProjectView(pvm: ProjectViewModel, changeType: ProjectChange = ProjectChange.NO_CHANGE) {
    //xxxDELETE
    console.log(`%cProjectService.EmitNextProjectView: changeType = ${changeType.toString()}; CurrentSolution.StartDate = ${pvm.Project['CurrentSolution']['StartDate']}`, "color:blue; background-color: lightgray;");
    pvm.ChangeType = changeType;
    this.SetCommittedData(pvm);
    this._projView.next(pvm);
    //pvm.Committed = null;
    pvm.Extra = null;
    console.log("_projView.observers: " + this._projView.observers.length + "; MoneyUnitChanged.observers: " + this.moneyUnitChange.observers.length + "; errorObject.observers: " + this.errorObject.observers.length);
  }

  //Helper functions
  public ChangeSizeFromChangedFunctionUnit(changedUnit: ChangedFunctionUnit): Observable<ChangedFunctionUnit> {
    //xxxDELETE
    //console.log("ProjectService.ChangeSizeFromChangedFunctionUnit (brefore send): changedUnit = " + JSON.stringify(changedUnit));
    let cfuSub = new Subject<ChangedFunctionUnit>();
    this.setSizePercentages(changedUnit);
    this.http.post<ChangedFunctionUnit>('api/projects/changefunctionunit', changedUnit, this.utilService.JsonOptions()).
      subscribe(cfu => {
        //console.log("ProjectService.ChangeSizeFromChangedFunctionUnit (on return): cfu = " + JSON.stringify(cfu));
        this._projView.value.Project["CurrentSolution"]["SizeUser"] = cfu.UserSize;
        this._projView.value.Project["CurrentSolution"]["SizeBase"] = cfu.BaseSize;
        this._projView.value.Project["FunctionUnit"] = cfu.FuncUnit;
        this._projView.value.Project["GearingFactor"] = cfu.NewGear;
        this._projView.value.SizeIsValid = cfu.BaseSize['effectiveSize'] >= AppConstants.MIN_SIZE && cfu.BaseSize['effectiveSize'] <= AppConstants.MAX_SIZE;
        this.EmitNextProjectView(this._projView.value, ProjectChange.FUNCTION_UNIT_OR_GEAR);
        cfuSub.next(cfu);
        cfuSub.complete();
      },
        error => {
          this.utilService.errorService.ErrorRedirect(error, 'Failed to Change Size From Changed FunctionUnit');
        }
      );
    return cfuSub;
  }

  protected setSizePercentages(changedUnit: ChangedFunctionUnit): void {
    // zzzASC - 23 Jan 2020
    // If this is a closeout project, set the values for sizing percentages before calling the controller (Estimation projects copy the values from the solution)
    if (this._projView.value.Project['Stage'] == Stage.CLOSEOUT) {
      if (changedUnit.UserSize['totalSize'] > 0) {
        changedUnit.PctNew = changedUnit.UserSize['sizeNewDouble'] > 0 ? Math.round((changedUnit.UserSize['sizeNewDouble'] / changedUnit.UserSize['totalSize']) * 10000) / 100 : null;
        changedUnit.PctMod = changedUnit.UserSize['sizeModDouble'] > 0 ? Math.round((changedUnit.UserSize['sizeModDouble'] / changedUnit.UserSize['totalSize']) * 10000) / 100 : null;
        changedUnit.PctReused = changedUnit.UserSize['sizeReusedDouble'] > 0 ? Math.round((changedUnit.UserSize['sizeReusedDouble'] / changedUnit.UserSize['totalSize']) * 10000) / 100 : null;
      }
      else {
        changedUnit.PctNew = 100;
        changedUnit.PctMod = null;
        changedUnit.PctReused = null;
      }
    }
  }

  public ChangeSize(changedUnit: ChangedFunctionUnit): Observable<ChangedFunctionUnit> {
    //xxxDELETE
    //console.log("ProjectService.ChangeSize: changedUnit (before send) = " + JSON.stringify(changedUnit));
    let cfuSub = new Subject<ChangedFunctionUnit>();
    this.setSizePercentages(changedUnit);
    this.http.post<ChangedFunctionUnit>('api/projects/changesize', changedUnit, this.utilService.JsonOptions()).
      subscribe(cfu => {
        //console.log("ProjectService.ChangeSize (on return): cfu = " + JSON.stringify(cfu));
        this._projView.value.Project["CurrentSolution"]["SizeUser"] = cfu.UserSize;
        this._projView.value.Project["CurrentSolution"]["SizeBase"] = cfu.BaseSize;
        this._projView.value.SizeIsValid = cfu.BaseSize['effectiveSize'] >= AppConstants.MIN_SIZE && cfu.BaseSize['effectiveSize'] <= AppConstants.MAX_SIZE;
        this.EmitNextProjectView(this._projView.value, ProjectChange.SIZING);
        cfuSub.next(cfu);
        cfuSub.complete();
      },
        error => {
          this.utilService.errorService.ErrorRedirect(error, 'Failed to change function unit size');
        }
      );
    return cfuSub;
  }

  public UpdateTrendInfo(trendId: number, versionId: number): void {
    //xxxDELETE
    //console.log("ProjectService.UpdateTrendInfo: TrendId = " + trendId + ", versionId = " + versionId);
    if (this._projView.value.Project['TrendConfiguration']['trendGroupId'] != trendId || this._projView.value.Project['TrendConfiguration']['trendVersionId'] != versionId) {
      var projChange = this._projView.value.Project['TrendConfiguration']['trendGroupId'] != trendId ? ProjectChange.TREND_GROUP : ProjectChange.TREND_VERSION;
      this._projView.value.Project['TrendConfiguration']['trendGroupId'] = trendId;
      this._projView.value.Project['TrendConfiguration']['trendVersionId'] = versionId;
      this._projView.value.Project['TrendGroupOnlyId'] = trendId;
      this.EmitNextProjectView(this._projView.value, projChange);
    }
  }

  public UpdateUserAccess(): Observable<UserView> {
    //xxxDELETE
    //console.log("ProjectService.UpdateUserAccess");
    let userViewSubject = new Subject<UserView>();
    this.http.post<AccessInfo>('api/projects/updateaccess', this._projView.value.Project, this.utilService.JsonOptions()).subscribe(accessInfo => {
      //console.log("ProjectService.UpdateUserAccess: accessInfo = " + JSON.stringify(accessInfo));
      this._projView.value.User.accessInfo = accessInfo;
      userViewSubject.next(this._projView.value.User);
      userViewSubject.complete();
    },
      error => {
        this.utilService.errorService.ErrorRedirect(error, 'Failed to update user access settings');
      }
    );
    return userViewSubject;
  }

  public UpdateSolutionMethod(methodId: number): void {
    //xxxDELETE
    //console.log("ProjectService.UpdateSolutionMethod: methodId = " + methodId);
    this._projView.value.Project['CurrentSolution']['SolutionMethod'] = methodId;
    this.EmitNextProjectView(this._projView.value, ProjectChange.SOLUTION_METHOD);
  }

  public UpdateProjectLaborRate(rate: number): void {
    //xxxDELETE
    //console.log("ProjectService.UpdateProjectLaborRate: rate = " + rate);
    this._projView.value.Project['AvgLaborRate'] = rate;
    this.EmitNextProjectView(this._projView.value, ProjectChange.LABOR_RATE);
  }

  public UpdatePhaseActiveSetting(phaseNdx: number, isActive: boolean): void {
    //xxxDELETE
    //console.log("ProjectService.UpdatePhaseActiveSetting: phaseNdx = " + phaseNdx + ", isActive = " + isActive);
    this._projView.value.Project["Phases"][phaseNdx]["active"] = isActive;
    this.EmitNextProjectView(this._projView.value, ProjectChange.ACTIVE_PHASE);
  }

  public FirstPhaseIndex(): number {
    return this._projView.value.Project["Phases"].findIndex(p => p['active']);
  }

  public UpdateProjectComparisonSolution(id: number): void {
    this._projView.value.Project['CurrentSolution']['ComparisonSolnId'] = id;
    this.UpdateProject(ProjectChange.COMPARISON_SOLUTION_CHANGE);
  }

  public UpdateProjectHistoryId(id: number): void {
    let newId = id > 0 ? id : null;
    if (newId !== this._projView.value.Project['AssociatedProjects']) {
      this._projView.value.Project['AssociatedProjects'] = newId;
      this.UpdateProject(ProjectChange.HISTORY_PROJECTS_CHANGE);
    }
  }

  public UpdateSizingIsValid(valid: boolean): void {
    this._projView.value.SizeIsValid = valid;
    this.EmitNextProjectView(this._projView.value, ProjectChange.SIZING);
  }

  public MakeSolutionCurrent(id: number): void {
    //xxxDELETE
    //console.log("ProjectService.Make Solution Current: id = " + id);
    if (this._projView.value.Project['CurrentSolution']['Id'] == id) {
      return;
    }
    this.http.post<ProjectViewModel>('api/projects/makecurrent', id, this.utilService.JsonOptions()).subscribe(pv => {
      this.InitializeAndEmit(pv, ProjectChange.CURRENT_SOLUTION_CHANGE);
    },
      theHttpError => {
        this._projInitialized.next(false);
        let theHttpErrorResponse = theHttpError as HttpErrorResponse;
        let serverError = this.utilService.errorService.getQsmServerError(theHttpErrorResponse);
        let errorHandled = false;
        if (serverError != null) {
          if (serverError.errorCode == ErrorConstants.UserAccessViolation) {
            this.handleQSMCustomErrors(theHttpError, 'MakeSolutionCurrent Access Violation');
            errorHandled = true;
          }
          else if (+(serverError.errorCode) == ProjectControllerErrors.MissingSolution) {
            alert("error code 1: " + serverError.errorMessage);
            errorHandled = true;
          }
        }
        if (errorHandled == false) {
          this.utilService.errorService.ErrorRedirect(theHttpError, 'Failed to make current');
        }
      }
    );
  }

  public LogCurrentSolution(name: string, desc: string, status: string): void {
    let solData = { thename: name, thedesc: desc, thestatus: status };
    //xxxDELETE
    //console.log("logging current solution with name: " + solData.thename);
    this.http.post<ProjectViewModel>('api/projects/log', solData, this.utilService.JsonOptions()).subscribe(pv => {
      pv.SizeIsValid = true;
      this.EmitNextProjectView(pv, ProjectChange.SOLUTION_LOGGED);
    },
      theHttpError => {
        this._projInitialized.next(false);
        let theHttpErrorResponse = theHttpError as HttpErrorResponse;
        let serverError = this.utilService.errorService.getQsmServerError(theHttpErrorResponse);
        let errorHandled = false;
        if (serverError != null) {
          if (serverError.errorCode == ErrorConstants.UserAccessViolation) {
            this.handleQSMCustomErrors(theHttpError, 'LogCurrentSolution Access Violation');
            errorHandled = true;
          }
          else if (+(serverError.errorCode) == ProjectControllerErrors.MissingSolution) {
            alert("error code 1: " + serverError.errorMessage);
            errorHandled = true;
          }
        }
        if (errorHandled == false) {
          this.utilService.errorService.ErrorRedirect(theHttpError, 'Failed to save log solution');
        }
      }
    );
  }

  public UpdateProject(reason: ProjectChange): void {
    //xxxDELETE
    //console.log("ProjectService.UpdateProject: reason = " + reason);
    // zzzASC - Note: array.findIndex requires ECMAScript 6
    let currSolnId = this._projView.value.Project['CurrentSolutionId'];
    let ndx = this._projView.value.Project["Solutions"].findIndex(function (soln) { return soln['Id'] == currSolnId; });
    if (ndx < 0) {
      //alert('Aborting project update. Current solution not found!');  // zzzASC - something more drastic should occur - this is SERIOUSLY BAD!
      let serverError = new QsmServerError();
      serverError.errorMessage = 'Current solution not found!';
      this.handleQSMCustomErrors(serverError, 'Current Solution not found!');
      return;
    }
    this._projView.value.Project["Solutions"][ndx] = this._projView.value.Project["CurrentSolution"];
    this.http.post<ProjectViewModel>('api/projects/update', this._projView.value.Project, this.utilService.JsonOptions()).subscribe(pv => {
      this.InitializeAndEmit(pv, reason);
    },
      theHttpError => {
        this._projInitialized.next(false);
        let theHttpErrorResponse = theHttpError as HttpErrorResponse;
        let serverError = this.utilService.errorService.getQsmServerError(theHttpErrorResponse);
        let errorHandled = false;
        if (serverError) {
          if (serverError.errorCode == ErrorConstants.UserAccessViolation) {
            errorHandled = true;
            this.handleQSMCustomErrors(theHttpError, 'UpdateProject Access Violation');

          }
          else if (+(serverError.errorCode) == ProjectControllerErrors.MissingSolution) {
            //alert("error code 1: " + serverError.errorMessage);
            this.handleQSMCustomErrors(theHttpError, 'Missing Solution!');
            errorHandled = true;
          }
          else if (serverError.extra) {
            //error is not handled, but project is reverted
            let pv = serverError.extra as ProjectViewModel;
            if (pv.Project) {
              this.EmitNextProjectView(pv, ProjectChange.REVERT);
              serverError.errorMessage += '  Your unsaved project changes have been reverted.';
            }
          }
        }
        if (errorHandled == false) {
          this.utilService.errorService.ErrorRedirect(theHttpError, 'Failed to Update Project');
        }
      }
    );
  }

  public SaveProject(saveCopy: boolean = false, doNotReplaceProject: boolean = false): void {
    //What is the equivalent of using #DEBUG.assert in typescript?
    if (doNotReplaceProject && !saveCopy) {
      console.error("Cloning a project cannot be called without saveCopy = true");
    }
    let url: string = 'api/projects/save';
    let projChange: ProjectChange = ProjectChange.SAVE;
    if (saveCopy) {
      url = 'api/projects/save/true';
      projChange = ProjectChange.SAVEAS;
    }
    this.http.post<ProjectViewModel>(url, null, this.utilService.JsonOptions()).subscribe(pv => {
      pv.SizeIsValid = true;
      if (!doNotReplaceProject) {
        this.EmitNextProjectView(pv, projChange);
      }
      else {
        this.newProjectInfo.next(pv.Project);
      }
    },
      theHttpError => {
        this._projInitialized.next(false);
        if (doNotReplaceProject) {
          this.newProjectInfo.next(null);
        }
        let theHttpErrorResponse = theHttpError as HttpErrorResponse;
        let serverError = this.utilService.errorService.getQsmServerError(theHttpErrorResponse);
        let errorHandled = false;
        if (serverError != null) {
          if (serverError.errorCode == ErrorConstants.UserAccessViolation) {
            this.handleQSMCustomErrors(theHttpError, 'SaveProject Access Violation');
            errorHandled = true;
          }
          else if (+(serverError.errorCode) == ProjectControllerErrors.MissingSolution) {
            alert("error code 1: " + serverError.errorMessage);
            errorHandled = true;
          }
          else if (serverError.errorCode == ErrorConstants.ConcurrencyViolation) {
            this.errorObject.next(serverError);
            errorHandled = true;
          }
        }
        if (errorHandled == false) {
          this.utilService.errorService.ErrorRedirect(theHttpError, "Failed to Save Project");
        }
      }
    );
  }

  public RunExternalCall(callId: number): Observable<ExternalGui> {
    let theGuiInfo = new Subject<ExternalGui>();
    this.http.post<ProjectViewModel>('api/projects/externalCall/' + callId + '/run', null, this.utilService.JsonOptions()).pipe(
      finalize(() => {
        //alert('external call finally!');
        theGuiInfo.complete();
      }))
      .subscribe(pv => {
        let theExtra = pv.Extra;
        if (theExtra) {
          let theExternalGui = theExtra.content as ExternalGui;
          theGuiInfo.next(theExternalGui);
        }
        if (pv.Project) {
          pv.SizeIsValid = true;
          this.EmitNextProjectView(pv, ProjectChange.SAVE);
        }
      },
        theHttpError => {
          this._projInitialized.next(false);
          let theHttpErrorResponse = theHttpError as HttpErrorResponse;
          let serverError = this.utilService.errorService.getQsmServerError(theHttpErrorResponse);
          if (serverError && serverError.extra ) {
            let pv = serverError.extra as ProjectViewModel;
            if (pv.Project) {
              pv.SizeIsValid = true;
              this.EmitNextProjectView(pv, ProjectChange.SAVE);
            }
          }
          let errorHandled = false;
          if (serverError != null) {
            if (serverError.errorCode == ErrorConstants.UserAccessViolation) {
              this.handleQSMCustomErrors(theHttpError, 'RunExternalCall Access Violation');
              errorHandled = true;
            }
            else if (+(serverError.errorCode) == ProjectControllerErrors.MissingSolution) {
              alert("error code 1: " + serverError.errorMessage);
              errorHandled = true;

            }
            else if (+(serverError.errorCode) == ProjectControllerErrors.ExternalCallError) {
              alert(serverError.errorMessage);
              errorHandled = true;
            }
          }
          if (!errorHandled) {
            this.utilService.errorService.ErrorRedirect(theHttpError);
          }
        });
    return theGuiInfo.asObservable();
  }

  public AdvanceProject(projId: number, solnId: number): void {
    //xxxDELETE
    //console.log("ProjectService.AdvanceProject");
    this.http.post<ProjectViewModel>(`api/projects/advance/${projId}/${solnId}`, this.utilService.JsonOptions()).subscribe(pv => {
      pv.SizeIsValid = true;
      this.EmitNextProjectView(pv, ProjectChange.ADVANCE);
    },
      theHttpError => {
        this._projInitialized.next(false);
        let theHttpErrorResponse = theHttpError as HttpErrorResponse;
        let serverError = this.utilService.errorService.getQsmServerError(theHttpErrorResponse);
        let errorHandled = false;
        if (serverError != null) {
          if (serverError.errorCode == ErrorConstants.UserAccessViolation) {
            errorHandled = true;
            this.handleQSMCustomErrors(theHttpError, 'AdvanceProject Access Violation');
          }
          else if (+(serverError.errorCode) == ProjectControllerErrors.MissingSolution) {
            //alert("error code 1: " + serverError.errorMessage);
            errorHandled = true;
            this.handleQSMCustomErrors(theHttpError, 'Missing Solution');
          }
        }
        if (errorHandled == false) {
          this.utilService.errorService.ErrorRedirect(theHttpError, "Failed to Advance Project");
        }
      });
  }

  public UpdateMoneyUnit(newMu: MonetaryUnit, newConversion: number, convert: boolean): void {
    let cmu = new ChangedMoneyUnit({
      newId: newMu.Id,
      newFactor: newConversion,
      oldId: this._projView.value.Project['ProjectMonetaryUnit']['Id'],
      oldFactor: this._projView.value.Project['MonetaryConversionFactor'],
      convert: convert
    });
    if (cmu.newId != cmu.oldId) {
      cmu.convert = true;
      this._projView.value.Project['ProjectMonetaryUnit'] = newMu;
    }
    else if (cmu.oldFactor == cmu.newFactor) {
      return;   // nothing to do
    }
    this._projView.value.Project['MonetaryConversionFactor'] = newConversion;
    this.moneyUnitChange.next(cmu);
  }

  public GetFriendlyUrl(title: string): Observable<string> {
    return this.http.get('api/projects/friendlyurl/' + title, {
      responseType: 'text'
    });
  }

  public saveHandler(formGroup, listSolutions): boolean {
    this.solutionChanges = formGroup.value;
    this.solutionId = formGroup.get('Id').value;
    let soln = listSolutions.find(s => s.Id === this.solutionId);
    let result: boolean = false;
    if (soln !== null && (this.solutionChanges['name'] != soln['name'] || this.solutionChanges['statusId'] != soln['statusId'] || this.solutionChanges['description'] != soln['description'])) {
      soln['name'] = this.solutionChanges['name'];
      soln['description'] = this.solutionChanges['description'];
      soln['statusId'] = this.solutionChanges['statusId'] == AppConstants.UNKNOWN_WORKFLOW_STATUS ? null : this.solutionChanges['statusId'];
      result = true;
    }
    return result;
  }

  public UpdateSolutionLog(listSolutions: BasicSolution[]): boolean {
    let change: boolean = false;
    if (this.projInitialized) {
      let projInfo = this._projView.value.Project;
      // check each logged solution in projInfo to see if it has been deleted or modified
      let solns = projInfo['Solutions'];
      let ndx = 0;
      while (ndx < solns.length) {
        let soln = solns[ndx++];
        if (!soln['isCurrent'] && soln['Id'] > 0) {
          let id = soln['Id'];
          let match = listSolutions.find(x => x['Id'] == id);
          if (match != null) {
            // zzzASC - note capitalization of 'statusId' is different for the two objects
            if (match['name'] != soln['name'] || match['description'] != soln['description'] || soln['StatusId'] != match['statusId']) {
              change = true;
              soln['StatusId'] = match['statusId'];
              soln['name'] = match['name'];
              soln['description'] = match['description'];
            }
          }
          else {
            // assume solution deleted?
            solns.splice(--ndx, 1);
            change = true;
          }
        }
      }
      if (change) {
        this.UpdateProject(ProjectChange.SOLUTION_LOG_EDITED);
      }
      else {
        // It is necessary to force a 'new' project view to be emitted (because there are objects that are waiting on the 'next' projectView)
        this.EmitNextProjectView(this._projView.value, ProjectChange.SOLUTION_LOG_EDITED);
      }
    }
    return change;
  }

  /*
   * zzzASC - Create a 'one time' subscription that waits for a specific type
   *          of change to the project (as suggested by Andy Berner)
   */
  public WaitForProjectChange(id: ProjectChange): Observable<ProjectViewModel> {
    this._projView.value.ChangeType = ProjectChange.NO_CHANGE;    // if the id passed in is ProjectChange.NO_CHANGE then they will get a result right away...
    return this.currentProjectView.pipe(first(pv => pv.ChangeType === id));
  }
}
